import { gql } from "@apollo/client";

// Fragment for linked reference with all document types
const LINKED_REFERENCE_FIELDS = gql`
  fragment LinkedReferenceFields on LinkedReference {
    collection
    documentId
    data {
      ... on TaskLinkedUser {
        _id
        firstName
        lastName
        email
        type
        role
        leadFields {
          isLead
          status
          type
          company
        }
      }
      ... on TaskLinkedAsset {
        _id
        assetType
        assetName
        friendlyId
        status
        anticipatedAum
        opportunityType
        closeDate
        owner {
          _id
        }
      }
      ... on TaskLinkedInteraction {
        _id
        summary
        communicationMethod
        interactionDate
      }
      ... on TaskLinkedEntity {
        _id
        entityName
        entityType
      }
      ... on TaskLinkedTransaction {
        _id
        friendlyId
        transactionType
        transactionStatus
        transactionAmount
        owner {
          _id
        }
        denormalized {
          donorName
          senderName
          recipientName
        }
      }
      ... on TaskLinkedProduct {
        _id
        name
        productType
        productStatus
        ownerId
        entityId
      }
    }
  }
`;

// Light version of linked reference (for kanban/table views)
const LINKED_REFERENCE_FIELDS_LIGHT = gql`
  fragment LinkedReferenceFieldsLight on LinkedReference {
    collection
    documentId
    data {
      ... on TaskLinkedUser {
        _id
        firstName
        lastName
        type
        role
        leadFields {
          isLead
          status
        }
      }
      ... on TaskLinkedAsset {
        _id
        assetType
        assetName
        friendlyId
        status
        owner {
          _id
        }
      }
      ... on TaskLinkedEntity {
        _id
        entityName
        entityType
      }
      ... on TaskLinkedTransaction {
        _id
        friendlyId
        transactionType
        transactionStatus
        transactionAmount
        owner {
          _id
        }
        denormalized {
          donorName
          senderName
          recipientName
        }
      }
      ... on TaskLinkedProduct {
        _id
        name
        productType
        productStatus
        ownerId
        entityId
      }
    }
  }
`;

const PARENT_CHECKLIST_FIELDS = gql`
  fragment ParentChecklistFields on Task {
    parentChecklist {
      task { _id key summary }
      fieldId
      itemId
    }
    taskSchedule {
      schedule { _id summary }
      dueDate
      occurrence
    }
  }
`;

const TASK_BOARD_FIELDS = gql`
  fragment TaskBoardFields on TaskBoard {
    _id
    name
    prefix
    statuses { name order color isCompletion }
    roadmapOptions
    priorities { name color }
    fieldConfig {
      impact { enabled label scale { value label } }
      effort { enabled label scale { value label } }
      attachments { enabled }
      uploadLinks { enabled }
      linkedUsers { enabled }
      linkedEntities { enabled }
      linkedOpportunities { enabled }
      linkedTransactions { enabled }
      linkedProducts { enabled }
      copyBranchName { enabled }
    }
    customFields { id name type options scale { value label } required order placeholder description }
  }
`;

export const CREATE_TASK = gql`
  ${LINKED_REFERENCE_FIELDS}
  mutation CreateTask($input: CreateTaskInput!, $boardId: ID!) {
    createTask(input: $input, boardId: $boardId) {
      _id key taskNumber summary description status roadmap priority dueDate impact effort
      board { _id name prefix }
      assignee { _id firstName lastName email }
      requestedBy { _id firstName lastName email }
      createdBy { _id firstName lastName email }
      completionSubscribers { _id }
      linkedReferences { ...LinkedReferenceFields }
      customFieldValues createdAt updatedAt
    }
  }
`;

export const UPDATE_TASK = gql`
  ${LINKED_REFERENCE_FIELDS}
  ${PARENT_CHECKLIST_FIELDS}
  mutation UpdateTask($identifier: String!, $updates: UpdateTaskInput!) {
    updateTask(identifier: $identifier, updates: $updates) {
      _id key taskNumber summary description status roadmap priority dueDate impact effort
      completedAt completedBy { _id firstName lastName } archivedAt archivedBy { _id firstName lastName } order customFieldValues
      board { _id name prefix }
      assignee { _id firstName lastName }
      requestedBy { _id firstName lastName }
      createdBy { _id firstName lastName }
      completionSubscribers { _id }
      linkedReferences { ...LinkedReferenceFields }
      slack { channelId channelName state }
      ...ParentChecklistFields
      updatedAt
    }
  }
`;

export const GET_TASKS = gql`
  ${TASK_BOARD_FIELDS}
  ${LINKED_REFERENCE_FIELDS}
  ${PARENT_CHECKLIST_FIELDS}
  query GetTasks($boardId: ID, $status: String, $roadmap: String, $assignee: ID, $requestedBy: ID, $includeArchived: Boolean, $archivedOnly: Boolean, $linkedDocumentId: ID, $linkedCollection: String) {
    tasks(boardId: $boardId, status: $status, roadmap: $roadmap, assignee: $assignee, requestedBy: $requestedBy, includeArchived: $includeArchived, archivedOnly: $archivedOnly, linkedDocumentId: $linkedDocumentId, linkedCollection: $linkedCollection) {
      _id key taskNumber summary description status roadmap priority dueDate impact effort impactScore
      board { ...TaskBoardFields }
      assignee { _id firstName lastName email }
      requestedBy { _id firstName lastName email }
      createdBy { _id firstName lastName email }
      completionSubscribers { _id }
      linkedReferences { ...LinkedReferenceFields }
      ...ParentChecklistFields
      completedAt completedBy { _id firstName lastName } archivedAt archivedBy { _id firstName lastName } customFieldValues createdAt updatedAt
    }
  }
`;

export const GET_TASK = gql`
  ${TASK_BOARD_FIELDS}
  ${LINKED_REFERENCE_FIELDS}
  ${PARENT_CHECKLIST_FIELDS}
  query GetTask($identifier: String!) {
    task(identifier: $identifier) {
      _id key taskNumber summary description status roadmap priority dueDate impact effort impactScore
      board { ...TaskBoardFields }
      assignee { _id firstName lastName email }
      requestedBy { _id firstName lastName email }
      createdBy { _id firstName lastName email }
      completionSubscribers { _id }
      linkedReferences { ...LinkedReferenceFields }
      ...ParentChecklistFields
      completedAt completedBy { _id firstName lastName } archivedAt archivedBy { _id firstName lastName } customFieldValues createdAt updatedAt
    }
  }
`;

export const ARCHIVE_TASK = gql`
  mutation ArchiveTask($identifier: String!) {
    archiveTask(identifier: $identifier) { _id key archivedAt archivedBy { _id firstName lastName } }
  }
`;

export const UNARCHIVE_TASK = gql`
  mutation UnarchiveTask($identifier: String!) {
    unarchiveTask(identifier: $identifier) { _id key archivedAt archivedBy { _id firstName lastName } }
  }
`;

export const DELETE_TASK = gql`
  mutation DeleteTask($identifier: String!) {
    deleteTask(identifier: $identifier) { _id key }
  }
`;

// Lightweight query for kanban/table views - doesn't include full team config per task
export const GET_TASKS_LIGHT = gql`
  ${LINKED_REFERENCE_FIELDS_LIGHT}
  ${PARENT_CHECKLIST_FIELDS}
  query GetTasksLight($boardId: ID, $status: String, $roadmap: String, $assignee: ID, $requestedBy: ID, $includeArchived: Boolean, $archivedOnly: Boolean, $linkedDocumentId: ID, $linkedCollection: String) {
    tasks(boardId: $boardId, status: $status, roadmap: $roadmap, assignee: $assignee, requestedBy: $requestedBy, includeArchived: $includeArchived, archivedOnly: $archivedOnly, linkedDocumentId: $linkedDocumentId, linkedCollection: $linkedCollection) {
      _id key taskNumber summary description status roadmap priority dueDate impact effort
      completedAt completedBy { _id firstName lastName } archivedAt archivedBy { _id firstName lastName } order customFieldValues
      board { _id name prefix }
      assignee { _id firstName lastName }
      requestedBy { _id firstName lastName }
      createdBy { _id firstName lastName }
      completionSubscribers { _id }
      linkedReferences { ...LinkedReferenceFieldsLight }
      slack { channelId channelName state }
      ...ParentChecklistFields
      createdAt updatedAt
    }
  }
`;

// Query for TasksPanel - gets tasks linked to a specific user or asset
export const GET_LINKED_TASKS = gql`
  query GetLinkedTasks($linkedDocumentId: ID, $linkedCollection: String, $includeArchived: Boolean) {
    tasks(linkedDocumentId: $linkedDocumentId, linkedCollection: $linkedCollection, includeArchived: $includeArchived) {
      _id key taskNumber summary description status roadmap priority
      board { _id name prefix statuses { name order color isCompletion } priorities { name color } }
      completedAt completedBy { _id firstName lastName } archivedAt archivedBy { _id firstName lastName } order customFieldValues
      assignee { _id firstName lastName }
      requestedBy { _id firstName lastName }
      createdBy { _id firstName lastName }
      createdAt updatedAt
    }
  }
`;

// Lightweight list of active tasks for pickers (e.g. the upload-link task
// selector). Only fetches identity fields to keep the payload small.
export const GET_TASKS_FOR_LINKING = gql`
  query GetTasksForLinking {
    tasksForLinking {
      _id
      key
      summary
    }
  }
`;

export const TASK_ATTACHMENT_FIELDS = gql`
  fragment TaskAttachmentFields on TaskAttachment {
    _id
    fileName
    fileType
    fileCategory
    user {
      _id
      firstName
      lastName
      email
      type
      role
    }
    product {
      _id
      name
      productType
      productStatus
      ownerId
      entityId
    }
    asset {
      _id
      assetType
      assetName
      friendlyId
      status
      owner {
        _id
      }
    }
    entity {
      _id
      entityName
      entityType
    }
    transaction {
      _id
      friendlyId
      transactionType
      transactionStatus
      transactionAmount
      owner {
        _id
      }
      denormalized {
        donorName
        senderName
        recipientName
      }
    }
  }
`;

// Slim query for the edit modal to lazily load a task's attachment metadata.
export const GET_TASK_ATTACHMENTS = gql`
  ${TASK_ATTACHMENT_FIELDS}
  query GetTaskAttachments($identifier: String!) {
    task(identifier: $identifier) {
      _id
      key
      attachments {
        ...TaskAttachmentFields
      }
    }
  }
`;

export const ASSIGN_TASK_ATTACHMENT = gql`
  ${TASK_ATTACHMENT_FIELDS}
  mutation AssignTaskAttachment(
    $documentId: ID!
    $fileCategory: String!
    $collection: String!
    $linkedDocumentId: ID!
    $productId: ID
    $relatedReferences: [LinkedReferenceInput]
  ) {
    assignTaskAttachment(
      documentId: $documentId
      fileCategory: $fileCategory
      collection: $collection
      linkedDocumentId: $linkedDocumentId
      productId: $productId
      relatedReferences: $relatedReferences
    ) {
      ...TaskAttachmentFields
    }
  }
`;

export const REORDER_TASKS = gql`
  mutation ReorderTasks($tasks: [ReorderTaskInput!]!) {
    reorderTasks(tasks: $tasks) {
      _id key order
    }
  }
`;

export const BULK_ARCHIVE_TASKS = gql`
  mutation BulkArchiveTasks($boardId: ID!, $olderThan: Date!) {
    bulkArchiveTasks(boardId: $boardId, olderThan: $olderThan) {
      archivedCount
      archivedKeys
    }
  }
`;

export const CREATE_TASK_SLACK_CHANNEL = gql`
  mutation CreateTaskSlackChannel($identifier: String!) {
    createTaskSlackChannel(identifier: $identifier) {
      _id key
      slack { channelId channelName state }
    }
  }
`;

export const ARCHIVE_TASK_SLACK_CHANNEL = gql`
  mutation ArchiveTaskSlackChannel($identifier: String!) {
    archiveTaskSlackChannel(identifier: $identifier) {
      _id key
      slack { channelId channelName state }
    }
  }
`;

export const PROMOTE_CHECKLIST_ITEM = gql`
  mutation PromoteChecklistItem($parentIdentifier: String!, $fieldId: String!, $item: JSON!) {
    promoteChecklistItem(parentIdentifier: $parentIdentifier, fieldId: $fieldId, item: $item) {
      _id key customFieldValues
    }
  }
`;

export const UNLINK_CHECKLIST_ITEM = gql`
  mutation UnlinkChecklistItem($parentIdentifier: String!, $fieldId: String!, $itemId: String!) {
    unlinkChecklistItem(parentIdentifier: $parentIdentifier, fieldId: $fieldId, itemId: $itemId) {
      _id key customFieldValues
    }
  }
`;
